import config from "appConfig";

const theory = `
<h3 class=sectionHead>Airborne sound insulation</h3>
<p>In rooms in buildings, such as dwellings, the sound is transmitted through walls, floors, ceilings and small building elements. When the sound energy hits a surface of a wall or any other element of the buildings, it is partly reflected and partly dissipated into heat within the material of the wall. But some sound energy propagates through the building elements towards other connecting structures. Finally, some energy is transmitted to other parts of the building and radiated into the so-called “receiving room”. In Sections 5.16 and Section 5.24, the quantities are introduced which characterize the airborne sound transmission. With the opportunity of auralization, the information depth of sound insulation problems can be expanded from single-number quantities to an auditory impression.<br class=newline>
<p>Here, we discuss the fundamental concepts of sound insulation prediction techniques from the filter design and auralization perspective. Usually, the frequency range of the one-third octave bands from 50 to 5000 Hz is considered. Airborne sound insulation tends to be lowest in the low frequency range and highest in the high-frequency range (i.e. acting as low pass filter). Hence, significant transmission of airborne sound above 5000 Hz is usually not an issue as far as the predictions of sound insulation metrics are concerned.<br class=newline>
<p>The principle of sound insulation auralization is to simulate the alteration of a sound signal from its source to the receiver end via transmission through the building elements involved in transmission of sound energy. The simulation results are then applied for impulse response filter design. The prerequisite for sound insulation filters design is an accurate sound insulation model which is supposed to be based on the sound insulation prediction theory as well as on available international standards and/or in-situ measurements. Most of the input data for filters design and auralization can be taken from standardized data formats of sound insulation (for example, ISO 12354-1 [<span class=cmbx-10>iso12354-1-2017</span>] and ISO 12354-3 [<span class=cmbx-10>iso12354-3-2017</span>]) from where point to point transfer functions from source to the listener can be derived.
<h4 class=subsectionHead>Sound insulation models for adjacent rooms</h4>
<p>
   The most used methods for sound insulation prediction are based on ISO 12354 (Part 1) [<span class=cmbx-10>iso12354-1-2017</span>]. As an established method, ISO 12354-1 [<span class=cmbx-10>iso12354-1-2017</span>] is the foundation for the sound insulation auralization model and for filter design. According to the standard, the transmission coefficients of building elements can be expressed by
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>τ</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ij</mi>
         </mrow>
      </msub>
   </math>
   , which is the energy ratio of the incident sound energy in the source room to the transmitted energy in the receiver room. This is defined for all transmission paths ij between two rooms. In Figure <a href=#x1-2001r1>1</a>, a typical adjacent source and receiver room, separated by a wall element are shown.
   <figure class=figure>
<p><img alt="Figure 1. Typical adjacent source and receiving rooms with receiving room walls represented as secondary point sources radiating elements" src=${config.ASSETS_URL}/03/13/theory/figure-1.svg><figcaption class=caption>Figure 1. Typical adjacent source and receiving rooms with receiving room walls represented as secondary point sources radiating elements</figcaption></figure>
<p>
   The resulting average sound pressure
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>R</mi>
         </mrow>
      </msub>
   </math>
   in the receiver room is calculated for all transmission paths by Equation <a href=#x1-2002r1>1</a>. Here i and j denote the source and receiving room wall elements respectively for the transmission path ij, with receiving room volume V and the separating (direct) element area
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>D</mi>
         </mrow>
      </msub>
   </math>
   between the two rooms.
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>S</mi>
         </mrow>
      </msub>
   </math>
   is the mean squared sound pressure in the receiving room and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>T</mi>
   </math>
   is the reverberation time of the receiver room.
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mrow>
               <msubsup>
                  <mrow>
                     <mi>p</mi>
                  </mrow>
                  <mrow>
                     <mi>R</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msubsup>
               <mo class=MathClass-rel stretchy=false>=</mo>
               <msubsup>
                  <mrow>
                     <mi>p</mi>
                  </mrow>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msubsup>
               <mfrac>
                  <mrow>
                     <msub>
                        <mrow>
                           <mi>S</mi>
                        </mrow>
                        <mrow>
                           <mi>D</mi>
                        </mrow>
                     </msub>
                  </mrow>
                  <mrow>
                     <mn>0.32</mn>
                     <mi>V</mi>
                  </mrow>
               </mfrac>
               <mfrac>
                  <mrow>
                     <mi>T</mi>
                  </mrow>
                  <mrow>
                     <mn>0.5</mn>
                  </mrow>
               </mfrac>
               <munder class=msub>
                  <mrow>
                     <mo>∑</mo>
                  </mrow>
                  <mrow>
                     <mi class=MathClass-op>∀</mi>
                     <mo>⁡</mo>
                     <mi mathvariant=italic>ij</mi>
                  </mrow>
               </munder>
               <msub>
                  <mrow>
                     <mi>τ</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>ij</mi>
                  </mrow>
               </msub>
            </mrow>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   From these equations, the sound power from the radiating elements on the receiving room walls are computed. In the most simple case they act as point sources located at the centres of the walls. Also in the receiving room happens to be a diffuse field with reverberation. The balance between the direct and the reverberant part of sound field is very important for the sound perception in the receiving rooms. This energy balance is obtained by distributing the receiver sound pressure into direct and reverberant (diffuse) sound field. It is computed through the ratio of energies given by relationship,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mrow>
         <mfrac>
            <mrow>
               <msubsup>
                  <mrow>
                     <mi>p</mi>
                  </mrow>
                  <mrow>
                     <mi>R</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msubsup>
            </mrow>
            <mrow>
               <msubsup>
                  <mrow>
                     <mi>p</mi>
                  </mrow>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msubsup>
            </mrow>
         </mfrac>
      </mrow>
   </math>
   =
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mrow>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>E</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>rev</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>E</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>dir</mi>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
      </mrow>
   </math>
   =
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mrow>
         <mfrac>
            <mrow>
               <mn>16</mn>
               <mi>π</mi>
               <msup>
                  <mrow>
                     <mi>r</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
            <mrow>
               <mi>A</mi>
            </mrow>
         </mfrac>
      </mrow>
   </math>
   . The quantities,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>E</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>dir</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>E</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>rev</mi>
         </mrow>
      </msub>
   </math>
   are the energies of direct and reverb sound field at a distance r from the wall. A is the equivalent absorption area of the receiving room.<br class=newline>
<p>
   The temporal effects of the receiving rooms in terms of reverberation are included by the measured or simulated impulse response,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mrow>
         <mi>h</mi>
         <mo class=MathClass-open stretchy=false>(</mo>
         <mi>t</mi>
         <mo class=MathClass-close stretchy=false>)</mo>
      </mrow>
   </math>
   of this room. Here, at first, the direct sound is removed from the impulse response as it is already included in the transmission path calculation in its binaural form
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mrow>
         <mi mathvariant=italic>HRIR</mi>
         <mstyle mathsize=1.19em>
            <mrow>
               <mo fence=true form=prefix>(</mo>
            </mrow>
         </mstyle>
         <mi>t</mi>
         <mo class=MathClass-bin stretchy=false>−</mo>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>r</mi>
                  </mrow>
                  <mrow>
                     <mi>j</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>c</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
         <mo class=MathClass-punc stretchy=false>,</mo>
         <msub>
            <mrow>
               <mi>𝜃</mi>
            </mrow>
            <mrow>
               <mi>j</mi>
            </mrow>
         </msub>
         <mo class=MathClass-punc stretchy=false>,</mo>
         <msub>
            <mrow>
               <mi>φ</mi>
            </mrow>
            <mrow>
               <mi>j</mi>
            </mrow>
         </msub>
         <mstyle mathsize=1.19em>
            <mrow>
               <mo fence=true form=prefix>)</mo>
            </mrow>
         </mstyle>
      </mrow>
   </math>
   . Subsequently, it is approximately equalised to white spectrum and normalised in energy. Overall, the binaural impulse response for transmission from source to receiver of the transmission path ij is given in Equation <a href=#x1-2003r2>2</a> [<span class=cmbx-10>thaden˙auralisation˙2005</span>]. All binaural contributions from the radiating wall elements are summed up to get the final signal which is shown in Figure <a href=#x1-2004r2>2</a> (left) and Figure <a href=#x1-2004r2>2</a> (right) in frequency and time domain respectively.
<table class=equation >
   <tr>
      <td style="font-size:4px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML >
            <mrow>
               <msub>
                  <mrow>
                     <mi>h</mi>
                  </mrow>
                  <mrow>
                     <mi>R</mi>
                     <mo class=MathClass-punc stretchy=false>,</mo>
                     <mi mathvariant=italic>ij</mi>
                  </mrow>
               </msub>
               <mo class=MathClass-open stretchy=false>(</mo>
               <mi>t</mi>
               <mo class=MathClass-close stretchy=false>)</mo>
               <mo class=MathClass-rel stretchy=false>=</mo>
               <msqrt>
                  <mrow>
                     <mfrac>
                        <mrow>
                           <msub>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>D</mi>
                              </mrow>
                           </msub>
                        </mrow>
                        <mrow>
                           <mn>0.32</mn>
                           <mi>V</mi>
                        </mrow>
                     </mfrac>
                     <mfrac>
                        <mrow>
                           <mi>T</mi>
                        </mrow>
                        <mrow>
                           <mn>0.5</mn>
                        </mrow>
                     </mfrac>
                     <msub>
                        <mrow>
                           <mi>τ</mi>
                        </mrow>
                        <mrow>
                           <mi mathvariant=italic>ij</mi>
                        </mrow>
                     </msub>
                  </mrow>
               </msqrt>
               <mstyle mathsize=2.45em>
                  <mrow>
                     <mo fence=true form=prefix>[</mo>
                  </mrow>
               </mstyle>
               <msqrt>
                  <mrow>
                     <mfrac>
                        <mrow>
                           <mi>A</mi>
                        </mrow>
                        <mrow>
                           <mn>16</mn>
                           <mi>π</mi>
                           <msubsup>
                              <mrow>
                                 <mi>r</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>ij</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msubsup>
                           <mo class=MathClass-bin stretchy=false>+</mo>
                           <mi>A</mi>
                        </mrow>
                     </mfrac>
                  </mrow>
               </msqrt>
               <mi mathvariant=italic>HRIR</mi>
               <mstyle mathsize=1.61em>
                  <mrow>
                     <mo fence=true form=prefix>(</mo>
                  </mrow>
               </mstyle>
               <mi>t</mi>
               <mo class=MathClass-bin stretchy=false>−</mo>
               <mfrac>
                  <mrow>
                     <msub>
                        <mrow>
                           <mi>r</mi>
                        </mrow>
                        <mrow>
                           <mi>j</mi>
                        </mrow>
                     </msub>
                  </mrow>
                  <mrow>
                     <msub>
                        <mrow>
                           <mi>c</mi>
                        </mrow>
                        <mrow>
                           <mn>0</mn>
                        </mrow>
                     </msub>
                  </mrow>
               </mfrac>
               <mo class=MathClass-punc stretchy=false>,</mo>
               <msub>
                  <mrow>
                     <mi>𝜃</mi>
                  </mrow>
                  <mrow>
                     <mi>j</mi>
                  </mrow>
               </msub>
               <mo class=MathClass-punc stretchy=false>,</mo>
               <msub>
                  <mrow>
                     <mi>φ</mi>
                  </mrow>
                  <mrow>
                     <mi>j</mi>
                  </mrow>
               </msub>
               <mstyle mathsize=1.61em>
                  <mrow>
                     <mo fence=true form=prefix>)</mo>
                  </mrow>
               </mstyle>
               <mo class=MathClass-bin stretchy=false>+</mo>
               <msqrt>
                  <mrow>
                     <mfrac>
                        <mrow>
                           <mn>16</mn>
                           <mi>π</mi>
                           <msubsup>
                              <mrow>
                                 <mi>r</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>ij</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msubsup>
                        </mrow>
                        <mrow>
                           <mn>16</mn>
                           <mi>π</mi>
                           <msubsup>
                              <mrow>
                                 <mi>r</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>ij</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msubsup>
                           <mo class=MathClass-bin stretchy=false>+</mo>
                           <mi>A</mi>
                        </mrow>
                     </mfrac>
                  </mrow>
               </msqrt>
               <mi>h</mi>
               <mo class=MathClass-open stretchy=false>(</mo>
               <mi>t</mi>
               <mo class=MathClass-close stretchy=false>)</mo>
               <mstyle mathsize=2.45em>
                  <mrow>
                     <mo fence=true form=prefix>]</mo>
                  </mrow>
               </mstyle>
            </mrow>
         </math>
      <td class=eq-no>(2)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 2. Final binaural signal at the listener: Frequency domain (left) and time domain (right) representation" src=${config.ASSETS_URL}/03/13/theory/figure-2.svg>
      <figcaption class=caption>Figure 2. Final binaural signal at the listener: Frequency domain (left) and time domain (right) representation</figcaption>
</figure>
<p>This impulse response can be convolved with any input signal in order to obtain the binaural output, which can be reproduced via headphones, for example.<br class=newline>

 <h3 class=likesectionHead>References</h3>
         <dl>
             <dt>[1]</dt><dd>ISO 12354-1. “Building acoustics–estimation of acoustic performance of
buildings from the performance of elements–part 1: airborne sound insula-
tion between rooms”. In: (2017).</dd>
             <dt>[2]</dt><dd>ISO 12354-3. “Building acoustics: estimation of acoustic performance of
buildings from the performance of elements - Part 3: airborne sound insu-
lation against outdoor sound”. In: (2017).</dd>
             <dt>[3]</dt><dd>Rainer Thaden. “Auralisation in building acoustics”. PhD Thesis. Aachen:
Publikationsserver der RWTH Aachen University, 2005. url: https : / /
publications.rwth-aachen.de/record/59629.</dd>
         </dl>
`;

export default theory;
